
import { useLocation, useNavigate } from "react-router-dom";
import { publicRoutes } from "@/constants/routes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import { debounce } from "lodash";
import { useLanguage } from "@/components/LanguageContext";

type Profile = {
  is_student: boolean;
  is_trainer: boolean;
};

export const useAuthRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();

  const isPublicRoute = () => {
    const currentPath = location.pathname;
    // Verificar se a rota atual está na lista de rotas públicas
    if (publicRoutes.includes(currentPath)) return true;
    
    // Verificar se a rota atual corresponde a algum padrão de rota pública
    return publicRoutes.some(route => {
      // Tratar rotas com parâmetros dinâmicos como /pp/:publicUrl
      if (route.includes(':')) {
        const routePattern = route.split(':')[0];
        return currentPath.startsWith(routePattern);
      }
      return false;
    });
  };

  const debouncedNavigate = debounce((path: string) => {
    navigate(path);
  }, 300);

  const handleAuthError = async (error: any) => {
    console.error("Auth error:", error);
    
    try {
      await supabase.auth.signOut();
      // Limpar dados de sessão, preservando preferências do utilizador (cookies, tema, etc.)
      const cookieConsent = localStorage.getItem('cookie-consent');
      const theme = localStorage.getItem('theme');
      localStorage.clear();
      if (cookieConsent) localStorage.setItem('cookie-consent', cookieConsent);
      if (theme) localStorage.setItem('theme', theme);
    } catch (signOutError) {
      console.error("Error during sign out:", signOutError);
    }
    
    if (!isPublicRoute()) {
      toast.error("A sua sessão expirou. Por favor, faça login novamente.");
      
      // Language-based auth redirects
      const authRoute = location.pathname.includes('student-dashboard') 
        ? (language === 'pt' ? "/pt/student-auth" : 
           language === 'es' ? "/es/student-auth" : 
           language === 'fr' ? "/fr/student-auth" : 
           "/student-auth")
        : (language === 'pt' ? "/pt/auth" : 
           language === 'es' ? "/es/auth" : 
           language === 'fr' ? "/fr/auth" : 
           "/auth");
        
      debouncedNavigate(authRoute);
    }
  };

  const checkUserTypeAndRedirect = async (user: User, profile: Profile & { is_onboarded?: boolean }) => {
    try {
      const isStudentRoute = location.pathname.includes('/student-dashboard') || location.pathname.includes('/student-auth');
      const isTrainerRoute = location.pathname.includes('/dashboard') && !location.pathname.includes('/student-dashboard');
      const isAuthRoute = location.pathname.includes('/auth');
      const isCompleteProfileRoute = location.pathname.includes('/complete-profile');
      
      let langPrefix = '';
      if (language === 'pt') langPrefix = '/pt';
      else if (language === 'es') langPrefix = '/es';
      else if (language === 'fr') langPrefix = '/fr';

      // Se o perfil não está completo, redirecionar para complete-profile
      if (!profile.is_onboarded && !isCompleteProfileRoute) {
        debouncedNavigate(`${langPrefix}/complete-profile`);
        return;
      }

      // Se está na página de complete-profile mas já está onboarded, redirecionar
      if (profile.is_onboarded && isCompleteProfileRoute) {
        if (profile.is_student && !profile.is_trainer) {
          debouncedNavigate(`${langPrefix}/student-dashboard`);
        } else {
          debouncedNavigate(`${langPrefix}/dashboard`);
        }
        return;
      }

      // Verificar se está numa rota pública (homepage, landing, etc.) e redirecionar para o dashboard
      const isOnPublicRoute = isPublicRoute() || location.pathname === '/' || location.pathname === `${langPrefix}/` || location.pathname === langPrefix;

      // Se o utilizador é apenas aluno
      if (profile.is_student && !profile.is_trainer) {
        if (isTrainerRoute || isAuthRoute || isOnPublicRoute) {
          debouncedNavigate(`${langPrefix}/student-dashboard`);
        }
      } 
      // Se o utilizador é apenas trainer
      else if (profile.is_trainer && !profile.is_student) {
        if (isStudentRoute || isAuthRoute || isOnPublicRoute) {
          debouncedNavigate(`${langPrefix}/dashboard`);
        }
      }
      else if (isAuthRoute || isOnPublicRoute) {
        debouncedNavigate(`${langPrefix}/dashboard`);
      }
    } catch (error) {
      console.error("Erro ao verificar tipo de usuário:", error);
      handleAuthError(error);
    }
  };

  return {
    isPublicRoute,
    handleAuthError,
    checkUserTypeAndRedirect
  };
};
